/**
 * VoiceEditor — every parameter of one voice, over the matrix.
 *
 * The matrix shows the nine parameters that shape a voice. This is the rest:
 * an overlay panel of knobs grouped by the engine's own sections, opened from
 * a row's deep-edit affordance and closed straight back to the grid.
 */

import { Knob } from './Knob.js';
import {
  ENGINE_EXPANDED_PARAMS,
  ENGINE_DISPLAY_NAMES,
  VOICE_HUES,
  toUiValue,
} from '../engine-data.js';

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class VoiceEditor {
  constructor({ engineType, index, total, getValue, onChange, onClose }) {
    this.engineType = engineType;
    this.hue = VOICE_HUES[engineType] || '#00ff88';
    this.getValue = getValue || (() => undefined);
    this.onChange = onChange || (() => {});
    this.onClose = onClose || (() => {});
    this.knobs = new Map();

    this.element = this.render(index, total);
  }

  render(index, total) {
    const panel = el('div', 'expanded-module');
    panel.dataset.engine = this.engineType;
    panel.style.setProperty('--hue', this.hue);
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute(
      'aria-label',
      `${ENGINE_DISPLAY_NAMES[this.engineType] || this.engineType} — all parameters`
    );

    /* ---- header ---- */

    const header = el('div', 'expanded-header');
    header.appendChild(el('div', 'eh-tick'));

    const titles = el('div');
    titles.appendChild(
      el('div', 'eh-title', ENGINE_DISPLAY_NAMES[this.engineType] || this.engineType)
    );
    titles.appendChild(
      el(
        'div',
        'eh-sub',
        `ALL PARAMETERS · VOICE ${String(index + 1).padStart(2, '0')} / ${total}`
      )
    );
    header.appendChild(titles);

    const closeBtn = el('button', 'collapse-btn', 'CLOSE  ESC');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close parameter panel');
    closeBtn.addEventListener('click', () => this.onClose());
    header.appendChild(closeBtn);

    panel.appendChild(header);

    /* ---- sections of knobs ---- */

    const grid = el('div', 'expanded-params-grid');
    const sections = ENGINE_EXPANDED_PARAMS[this.engineType] || {};

    Object.entries(sections).forEach(([sectionName, params]) => {
      const card = el('div', 'param-category-card');
      card.appendChild(el('div', 'param-category-header', sectionName));

      const knobRow = el('div', 'param-category-knobs');
      params.forEach((param) => {
        const stored = this.getValue(this.engineType, param.name);
        const value =
          stored === undefined ? param.default : toUiValue(this.engineType, param.name, stored);

        const knob = new Knob({
          name: param.name,
          label: param.label,
          min: param.min,
          max: param.max,
          default: param.default,
          value,
          unit: param.unit || '',
          decimals: param.decimals ?? 2,
          accentColor: this.hue,
          size: 42,
          onChange: (next, name) => this.onChange(this.engineType, name, next),
        });

        this.knobs.set(param.name, knob);
        knobRow.appendChild(knob.getElement());
      });

      card.appendChild(knobRow);
      grid.appendChild(card);
    });

    panel.appendChild(grid);

    return panel;
  }

  /** Pull fresh values in after a preset load, randomise or undo. */
  sync() {
    this.knobs.forEach((knob, name) => {
      const stored = this.getValue(this.engineType, name);
      if (stored === undefined) return;
      knob.setValue(toUiValue(this.engineType, name, stored), false);
    });
  }

  /** Follow a single parameter changed from the matrix, without a full pass. */
  syncParam(paramName) {
    const knob = this.knobs.get(paramName);
    if (!knob) return;
    const stored = this.getValue(this.engineType, paramName);
    if (stored === undefined) return;
    knob.setValue(toUiValue(this.engineType, paramName, stored), false);
  }

  getEngineType() {
    return this.engineType;
  }

  getElement() {
    return this.element;
  }

  destroy() {
    this.knobs.forEach((knob) => knob.destroy());
    this.knobs.clear();
    this.element.remove();
  }
}
